import React, { useRef, useState, useEffect } from 'react';
import './App.css';
import './index.css';
import Titlebar from './Titlebar';
import play from './resources/play-solid.svg'
import pause from './resources/pause-solid.svg'
import { TrashIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import { apiUrl } from './config';

// Keep default spots data as fallback
const defaultSpots = [
    {
      spot_id: 1,
      type: 'spot', // One of Rotator or Spot
      cart_number: '90246-001', // cart number
      description: 'May the 4th be with you',
      customer: 'Mr. Nice Guys',
      customer_id: '123456',
      length: 30, // valid values are 10, 15, 30, 45, 60 in seconds 
      customer_email: 'lindsay.walton@example.com',
      script: '',
      status: 'approved', // One of written, review, modification, produced, approved 
      spot_media:
        'https://images.unsplash.com/photo-1517841905240-472988babdf9?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80',
    },
    // More people...
  ]

// Circular Progress Component
function CircularProgress({ percentage, size = 50, strokeWidth = 3 }) {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const strokeDasharray = circumference;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;

  return (
    <svg
      className="absolute top-0 left-0 transform -rotate-90 pointer-events-none"
      width={size}
      height={size}
    >
      {/* Background circle */}
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        stroke="rgba(24, 8, 8, 0.3)"
        strokeWidth={strokeWidth}
        fill="none"
      />
      {/* Progress circle */}
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        stroke="rgba(9, 151, 18, 0.9)"
        strokeWidth={strokeWidth}
        fill="none"
        strokeDasharray={strokeDasharray}
        strokeDashoffset={strokeDashoffset}
        strokeLinecap="round"
        className="transition-all duration-100 ease-out"
      />
    </svg>
  );
}

// Individual Spot Row Component with audio functionality
function SpotRow({ spot, isChecked, onCheckboxChange, onDelete }) {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Handle play/pause
  const handlePlayPause = async () => {
    if (!audioRef.current) return;

    try {
      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
      } else {
        setIsLoading(true);
        setError(null);
        await audioRef.current.play();
        setIsPlaying(true);
      }
    } catch (error) {
      console.error('Error playing audio:', error);
      setError('Failed to play audio');
      setIsPlaying(false);
    } finally {
      setIsLoading(false);
    }
  };

  // Audio event handlers
  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  const handleEnded = () => {
    setIsPlaying(false);
    setCurrentTime(0);
  };

  const handleError = () => {
    setError('Audio failed to load');
    setIsPlaying(false);
    setIsLoading(false);
  };

  // Handle view spot
  const navigate = useNavigate();
  const handleViewSpot = (spotId) => {
    navigate(`/spotview/${spotId}`);
  };

  // Handle edit spot
  const handleEditSpot = (spotId) => {
    navigate(`/spotedit/${spotId}`);
  };

  // Calculate progress percentage
  const progressPercentage = duration > 0 ? (currentTime / duration) * 100 : 0;

  // Get audio URL - try multiple possible fields
  const audioUrl = spot.spot_media || spot.audio_file || spot.file_link;
  const hasAudioUrl = audioUrl && audioUrl !== '';

  return (
    <tr key={spot.spot_id} className={isChecked ? 'bg-gray-50' : ''}>
      <td className="w-1.5 px-3 py-5 text-sm font-medium text-gray-900 sm:table-cell">
        <input
          className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
          type="checkbox"
          checked={isChecked}
          onChange={() => onCheckboxChange(spot.spot_id)}
        />
      </td>
      <td className="whitespace-nowrap px-3 py-5 text-sm text-gray-500">{spot.type}</td>
      <td className="whitespace-nowrap px-3 py-5 text-sm text-gray-500">{spot.cart_number}</td>
      <td className="whitespace-nowrap px-3 py-5 text-sm text-gray-500">{spot.cut}</td>
      <td className="whitespace-nowrap pr-3 py-5 text-sm text-gray-500">{spot.spot_title}</td>
      <td className="whitespace-nowrap pr-3 py-5 text-sm text-gray-500">{spot.description}</td>
      <td className="whitespace-nowrap px-3 py-5 text-sm text-gray-500">
        <span className="inline-flex items-center rounded-md bg-green-50 px-2 py-1 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20">
          {spot.status}
        </span>
      </td>
      <td className="whitespace-nowrap px-3 py-5 text-sm text-gray-500">{spot.length}s</td>
      <td className="whitespace-nowrap py-5 pl-4 pr-3 text-sm sm:pl-0">
        <div className="flex items-center space-x-3">
          {/* Audio Player with Circular Progress */}
          <div className="flex items-center space-x-3">
            {hasAudioUrl ? (
              <>
                {/* Circular Play Button with Progress Container */}
                <div className="relative w-10 h-10 flex items-center justify-center">
                  <button
                    onClick={handlePlayPause}
                    disabled={isLoading}
                    className="w-8 h-8 flex items-center justify-center rounded-full bg-green-400 text-white hover:bg-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors relative z-10"
                    title={isPlaying ? 'Pause' : 'Play'}
                  >
                    {isLoading ? (
                      <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    ) : (
                      <img 
                        src={isPlaying ? pause : play} 
                        className="w-4 h-4" 
                        alt={isPlaying ? 'Pause' : 'Play'} 
                      />
                    )}
                  </button>
                  
                  {/* Circular Progress Bar */}
                  {(isPlaying || currentTime > 0) && duration > 0 && (
                    <CircularProgress 
                      percentage={progressPercentage} 
                      size={40} 
                      strokeWidth={4} 
                    />
                  )}
                </div>

                {/* Hidden audio element */}
                <audio
                  ref={audioRef}
                  src={audioUrl}
                  onTimeUpdate={handleTimeUpdate}
                  onLoadedMetadata={handleLoadedMetadata}
                  onEnded={handleEnded}
                  onError={handleError}
                  preload="metadata"
                />

                {error && (
                  <span className="text-xs text-red-500">{error}</span>
                )}
              </>
            ) : (
              <div className="flex items-center space-x-2">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-300 text-gray-500">
                  <img src={play} className="w-4 h-4 opacity-50" alt="No audio" />
                </div>
                <span className="text-xs text-gray-400">No audio</span>
              </div>
            )}
          </div>

          {/* Script Link */}
          <div className="ml-4">
            {spot.copy_link ? (
              <a 
                href={spot.copy_link} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-indigo-600 hover:text-indigo-900 text-sm"
              >
                Script<span className="sr-only">, {spot.copy_link}</span>
              </a>
            ) : (
              <span className="text-gray-400 text-sm">No script</span>
            )}
          </div>
        </div>
      </td>

      <td className="relative whitespace-nowrap py-5 pl-3 pr-4 text-right text-sm font-medium sm:pr-0">
        <button 
          onClick={() => handleViewSpot(spot.spot_id)}
          className="text-indigo-600 hover:text-indigo-900 mr-2"
        >
          View<span className="sr-only">, Spot {spot.spot_id}</span>
        </button>
        &nbsp; | &nbsp; 
        <button 
          onClick={() => handleEditSpot(spot.spot_id)}
          className="text-indigo-600 hover:text-indigo-900"
        >
          Edit<span className="sr-only">, Spot {spot.spot_id}</span>
        </button>
      </td>
    </tr>
  );
}

function SpotTable(props) {
  const [checkedItems, setCheckedItems] = useState([]);
  const [spots, setSpots] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    current_page: 1,
    page_size: 10,
    total_pages: 1,
    total_count: 0
  });
  const navigate = useNavigate();

  // Add handleAddSpot function
  const handleAddSpot = () => {
    navigate('/spotadd');
  };

  // Fetch media/spots function with pagination
  const fetchMedia = async (page = 1, limit = 10) => {
    try {
      setIsLoading(true);
      setError(null);
      
      const response = await fetch(apiUrl(`/media/all?page=${page}&page_size=${limit}`));
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Handle paginated response structure
      const spotsData = data.data || data; // Support both paginated and non-paginated responses
      const paginationData = data.pagination || {
        current_page: page,
        page_size: limit,
        total_pages: 1,
        total_count: Array.isArray(spotsData) ? spotsData.length : 0
      };
      
      // Transform the data if needed to match your component's expected format
      const formattedSpots = Array.isArray(spotsData) ? spotsData.map(spot => ({
        spot_id: spot._id || spot.id,
        type: spot.type || 'spot',
        cart_number: spot.cart || spot.cart_number,
        description: spot.description || spot.spot_title,
        customer: spot.customer_name || spot.customer,
        customer_id: spot.customer_id,
        length: spot.run_length || spot.length,
        customer_email: spot.customer_email || '',
        script: spot.script || '',
        status: spot.status || 'pending',
        spot_media: spot.audio_file || spot.file_link || spot.spot_media,
        spot_title: spot.spot_title,
        cut: spot.cut,
        isci_code: spot.isci_code,
        vocal_style: spot.vocal_style,
        copy_link: spot.copy_link
      })) : [];
      
      setSpots(formattedSpots);
      setPagination(paginationData);
      console.log('Media fetched successfully:', formattedSpots);
      
    } catch (error) {
      console.error('Error fetching media:', error);
      setError(error.message);
      // Keep default spots on error
      setSpots(defaultSpots);
      setPagination({
        current_page: 1,
        page_size: 10,
        total_pages: 1,
        total_count: defaultSpots.length
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch spots on component mount
  useEffect(() => {
    fetchMedia(pagination.current_page, pagination.page_size);
  }, [pagination.current_page, pagination.page_size]);

  // Handle page changes
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.total_pages && newPage !== pagination.current_page) {
      fetchMedia(newPage, pagination.page_size);
      setCheckedItems([]); // Clear selections when changing pages
    }
  };

  // Handle items per page changes
  const handleItemsPerPageChange = (newLimit) => {
    fetchMedia(1, newLimit); // Reset to page 1 when changing items per page
    setCheckedItems([]);
  };

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    const { current_page, total_pages } = pagination;

    let startPage = Math.max(1, current_page - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(total_pages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    
    return pages;
  };

  const handleCheckboxChange = (spotId) => {
    setCheckedItems(prev => {
      if (prev.includes(spotId)) {
        return prev.filter(id => id !== spotId);
      } else {
        return [...prev, spotId];
      }
    });
  };

  const deleteSpots = async () => {
    if (checkedItems.length === 0) return;
    

    console.log('Deleting spots with IDs:', checkedItems);

    if (window.confirm(`Are you sure you want to delete ${checkedItems.length} spot(s)?`)) {
      try {
        // Delete each selected spot
        await Promise.all(
          checkedItems.map(spotId => 
            fetch(apiUrl(`/media/delete/${spotId}`), {
              method: 'DELETE'
            })
          )
        );
        
        // Refresh the current page
        fetchMedia(pagination.current_page, pagination.page_size);
        setCheckedItems([]);
        
        console.log('Spots deleted successfully');
      } catch (error) {
        console.error('Error deleting spots:', error);
        alert('Failed to delete spots');
      }
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="lg:pl-72 sm:ml-0 px-0 sm:px-0">
        <Titlebar title="Spots"/>
        <div className="m-3 px-4 sm:px-6 lg:px-8">
          <div className="text-center py-12">
            <div className="text-gray-500">Loading spots...</div>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="lg:pl-72 sm:ml-0 px-0 sm:px-0">
        <Titlebar title="Spots"/>
        <div className="m-3 px-4 sm:px-6 lg:px-8">
          <div className="text-center py-12">
            <p className="text-red-500">Error loading spots: {error}</p>
            <button
              onClick={() => fetchMedia(pagination.current_page, pagination.page_size)}
              className="mt-4 rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="lg:pl-72 sm:ml-0 px-0 sm:px-0">
    <Titlebar title="Spots"/>
    
    <div className="m-3 px-4 sm:px-6 lg:px-8">
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <p className="mt-2 text-sm text-gray-700">
            Showing {spots.length} of {pagination.total_count} spots
          </p>
        </div>
      </div>
      
      <div className="mt-8 flow-root">
        <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
            {spots && Array.isArray(spots) ? (
              <table className="min-w-full divide-y divide-gray-300">
                <thead>
                  <tr>
                    <th className="w-1.5 px-3 py-3 text-sm font-semibold text-gray-900 sm:table-cell">
                      <button
                        type="button"
                        onClick={deleteSpots}
                        disabled={!checkedItems.length > 0}
                        className="rounded-full bg-indigo-600 p-1 text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50"
                      >
                        <TrashIcon aria-hidden="true" className="h-5 w-5" />
                      </button>
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      Type
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      Cart
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      Cut
                    </th>
                    <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-0">
                      Title
                    </th>
                    <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-0">
                      Description
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      Status
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      Length
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      Audio & Script
                    </th>
                    <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-0">
                      <button
                        type="button"
                        onClick={handleAddSpot}
                        className="block rounded-md bg-indigo-600 px-3 py-2 text-center text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                      >
                        Add Spot
                      </button>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {spots.map((spot) => (
                    <SpotRow
                      key={spot.spot_id}
                      spot={spot}
                      isChecked={checkedItems.includes(spot.spot_id)}
                      onCheckboxChange={handleCheckboxChange}
                      onDelete={deleteSpots}
                    />
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="text-center py-12">
                <div className="text-gray-500">
                  {spots === null ? 'No spots data available.' : 'Unable to load spots.'}
                </div>
              </div>
            )}
            
            {spots && Array.isArray(spots) && spots.length === 0 && (
              <div className="text-center py-12">
                <div className="text-gray-500">No spots found.</div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Pagination Controls */}
      {(pagination.total_pages > 1 || true) && (
        <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6">
          <div className="flex flex-1 justify-between sm:hidden">
            <button
              onClick={() => handlePageChange(pagination.current_page - 1)}
              disabled={pagination.current_page <= 1}
              className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button
              onClick={() => handlePageChange(pagination.current_page + 1)}
              disabled={pagination.current_page >= pagination.total_pages}
              className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
          <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
            <div className="flex items-center space-x-2">
              <p className="text-sm text-gray-700">
                Page <span className="font-medium">{pagination.current_page}</span> of{' '}
                <span className="font-medium">{pagination.total_pages}</span>
                <span className="text-xs text-gray-500 ml-2">
                  (Total: {pagination.total_count} spots)
                </span>
              </p>
              <select
                value={pagination.page_size}
                onChange={(e) => handleItemsPerPageChange(parseInt(e.target.value))}
                className="rounded-md border-gray-300 text-sm focus:border-indigo-500 focus:ring-indigo-500"
              >
                <option value={5}>5 per page</option>
                <option value={10}>10 per page</option>
                <option value={25}>25 per page</option>
                <option value={50}>50 per page</option>
              </select>
            </div>
            <div>
              <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                <button
                  onClick={() => handlePageChange(pagination.current_page - 1)}
                  disabled={pagination.current_page <= 1}
                  className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="sr-only">Previous</span>
                  <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
                </button>
                
                {getPageNumbers().map((pageNum) => (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${
                      pageNum === pagination.current_page
                        ? 'z-10 bg-indigo-600 text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600'
                        : 'text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0'
                    }`}
                  >
                    {pageNum}
                  </button>
                ))}
                
                <button
                  onClick={() => handlePageChange(pagination.current_page + 1)}
                  disabled={pagination.current_page >= pagination.total_pages}
                  className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="sr-only">Next</span>
                  <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}
    </div>
    </div>
    )
}

export default SpotTable;